import { useCallback, useState } from "react";
import { message } from "antd";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { bulkImportCustomList } from "src/services/AudienceListAPIs";
import { CSV_EXTENSION } from "../CreateAudience/CreateList/createList.constants";

const isCsvFile = (file: File) => file.name.toLowerCase().endsWith(CSV_EXTENSION);

const readErrorDetail = (error: unknown) =>
  (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;

export const useAudienceListImport = (listId: number, onImported: () => void) => {
  const { t } = useTranslation();
  const [importing, setImporting] = useState(false);

  const label = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      t(`audienceOverview.listModal.${key}`, options),
    [t],
  );

  const importFiles = useCallback(
    async (files: File[]) => {
      const csvFiles = files.filter(isCsvFile);
      if (csvFiles.length < files.length) {
        message.warning(label("importCsvOnly"));
      }
      if (!csvFiles.length) {
        return;
      }

      setImporting(true);
      let importedCount = 0;
      let addedCount = 0;
      try {
        for (const file of csvFiles) {
          try {
            // Sequential so the server dedups each file against the previous ones.
            const response = await bulkImportCustomList(listId, file);
            addedCount += response.added ?? 0;
            importedCount += 1;
          } catch (error) {
            const reason = readErrorDetail(error) || label("importErrorFallback");
            message.error(
              importedCount
                ? label("importPartial", {
                    done: importedCount,
                    total: csvFiles.length,
                    name: file.name,
                    reason,
                  })
                : label("importFailed", { name: file.name, reason }),
            );
            return;
          }
        }
        message.success(label("importSuccess", { count: addedCount, files: importedCount }));
      } finally {
        setImporting(false);
        if (importedCount) {
          onImported();
        }
      }
    },
    [listId, label, onImported],
  );

  return { importing, importFiles };
};
